import { isRecord } from "../metadata/manifest-record";
import type { LeagueAchievementLeaderboardPublic } from "@softmax/observatory-client/types";
import { formatDuration } from "@softmax/product-ux/play-values";
import {
  computeNextAwardAt,
  formatTzAbbrev,
  parseRewardsSettings,
  resolveRewardsForDivision,
} from "../../play/state";
import {
  type PreviewRewards,
  type PreviewTrail,
} from "../content/preview-competition.types";
export function storeRewards({
  settings,
  divisionId,
  now,
}: {
  settings: unknown;
  divisionId: string | null;
  now: Date;
}): PreviewRewards | null {
  const rewards = resolveRewardsForDivision({
    raw: parseRewardsSettings(isRecord(settings) ? settings : null),
    divisionId,
  });
  const credits = rewards?.creditsByRank ?? null;
  const firstPrize = credits?.[0];

  if (!rewards || !credits || firstPrize === undefined) return null;

  const prize = `${Math.round(firstPrize)} credits`;
  const runnerUp =
    credits.length > 1
      ? `then ${credits
          .slice(1)
          .map((v) => Math.round(v))
          .join(" · ")}`
      : null;
  let nextAward: string | null = null;
  let nextAwardIn: string | null = null;
  let isDailyAward = false;

  if (
    rewards.enabled &&
    rewards.awardTime &&
    rewards.timeZone &&
    !rewards.awardScheduleMisconfigured
  ) {
    isDailyAward = true;
    const at = computeNextAwardAt({
      awardTime: rewards.awardTime,
      timeZone: rewards.timeZone,
      now,
    });

    if (at) {
      nextAward = `${rewards.awardTime} ${formatTzAbbrev({ timeZone: rewards.timeZone, at })}`;
      nextAwardIn = `in ${formatDuration(at.getTime() - now.getTime())}`;
    }
  }

  const prizeSub = isDailyAward
    ? runnerUp
      ? `daily · ${runnerUp}`
      : "daily"
    : runnerUp;
  return { prize, prizeSub, nextAward, nextAwardIn };
}
export function trailFor({
  board,
  playerId,
}: {
  board: LeagueAchievementLeaderboardPublic;
  playerId: string;
}): PreviewTrail {
  const mine = board.entries.find((e) => e.player_id === playerId) ?? null;
  const earned = (mine?.achievements ?? [])
    .map((a) => ({
      name: a.name,
      points: a.points,
      timesEarned: a.times_earned,
      firstEarnedAt: a.first_earned_at ?? null,
      lastEarnedAt: a.last_earned_at ?? null,
    }))
    .toSorted((a, b) =>
      (b.lastEarnedAt ?? "").localeCompare(a.lastEarnedAt ?? ""),
    );
  const ranked = board.entries
    .filter((e) => e.achievement_points > 0)
    .toSorted((a, b) => b.achievement_points - a.achievement_points);
  const rankedPlayer = ranked.find((entry) => entry.player_id === playerId);
  const points = rankedPlayer?.achievement_points ?? null;
  const ahead =
    points === null
      ? 0
      : ranked.filter((e) => e.achievement_points > points).length;
  return {
    earned,
    points: mine?.achievement_points ?? 0,
    catalogSize: board.catalog.length,
    rankByPoints: rankedPlayer === undefined ? null : ahead + 1,
    rankShared:
      points !== null &&
      ranked.filter((e) => e.achievement_points === points).length > 1,
    playersWithAny: ranked.length,
  };
}
